import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, useLayoutEffect, useState, type ReactNode } from "react";
import { Loader2 } from "lucide-react";

import { jsonLdScript, organizationJsonLd } from "@/components/site/seo-copy";
import appCss from "../styles.css?url";

const fallbackWrap = "cl flex min-h-screen items-center justify-center bg-[#F3F3F0] px-6";
const fallbackBrand = (
  <div className="mb-8 flex items-center justify-center gap-2.5 text-[24px] font-extrabold tracking-[-0.03em] text-[#0B2160]">
    <span aria-hidden className="block h-[11px] w-[11px] rounded-full bg-[#FF5A3C]" />
    closdd
  </div>
);
const fallbackPrimary =
  "inline-flex min-h-12 items-center justify-center rounded-[4px] bg-[#FF5A3C] px-6 text-[15px] font-extrabold text-[#0B2160]";
const fallbackSecondary =
  "inline-flex min-h-12 items-center justify-center rounded-[4px] border-2 border-[#0B2160] bg-white px-5 text-[15px] font-extrabold text-[#0B2160]";

function NotFoundComponent() {
  return (
    <div className={fallbackWrap}>
      <div className="max-w-lg text-center">
        {fallbackBrand}
        <p className="font-mono text-[13px] uppercase tracking-[0.12em] text-[#D6361C]">404</p>
        <h1 className="mt-2.5 text-[clamp(32px,4vw,46px)] font-extrabold leading-[1.05] tracking-[-0.03em] text-[#0B2160]">
          Page not found.
        </h1>
        <p className="mt-2.5 text-[17px] text-[#3A3A40]">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-7">
          <Link to="/" className={fallbackPrimary}>
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();

  return (
    <div className={fallbackWrap}>
      <div className="max-w-lg text-center">
        {fallbackBrand}
        <p className="font-mono text-[13px] uppercase tracking-[0.12em] text-[#D6361C]">Error</p>
        <h1 className="mt-2.5 text-[clamp(32px,4vw,46px)] font-extrabold leading-[1.05] tracking-[-0.03em] text-[#0B2160]">
          This page didn't load.
        </h1>
        <p className="mt-2.5 text-[17px] text-[#3A3A40]">
          Something went wrong on our end. You can try again or head back home.
        </p>
        <div className="mt-7 flex flex-wrap justify-center gap-3">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className={fallbackPrimary}
          >
            Try again
          </button>
          <a href="/" className={fallbackSecondary}>
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "CLOSDD" },
      { name: "description", content: "Stop hiring salespeople from CVs. Candidates don't just apply. They pitch themselves. Hear them sell before you interview them. Post a role free." },
      { name: "author", content: "CLOSDD" },
      { property: "og:title", content: "CLOSDD" },
      { property: "og:description", content: "Stop hiring salespeople from CVs. Candidates don't just apply. They pitch themselves. Hear them sell before you interview them. Post a role free." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "CLOSDD" },
      { name: "twitter:description", content: "Stop hiring salespeople from CVs. Candidates don't just apply. They pitch themselves. Hear them sell before you interview them. Post a role free." },
    ],
    scripts: [jsonLdScript(organizationJsonLd)],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
      { rel: "icon", type: "image/svg+xml", href: "/favicon.svg" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700&family=Inter:wght@300;400;500;600;700&family=Archivo:wght@400;500;600;800&family=JetBrains+Mono:wght@500&display=swap",
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

/**
 * Slim top-of-page progress bar, shown for the duration of any route
 * transition (including the time spent fetching a not-yet-preloaded
 * route's JS chunk) — without this, clicking a nav link or menu item gave
 * no feedback at all until the next page suddenly appeared, which read as
 * the site being stuck/jumpy rather than just loading.
 */
function RouteProgressBar() {
  const isLoading = useRouterState({ select: (s) => s.isLoading });
  const [visible, setVisible] = useState(false);
  const [pct, setPct] = useState(0);

  useEffect(() => {
    let raf = 0;
    let hideTimer: ReturnType<typeof setTimeout>;

    if (isLoading) {
      setVisible(true);
      setPct(15);
      const tick = () => {
        setPct((p) => (p < 85 ? p + (85 - p) * 0.08 : p));
        raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    } else {
      setPct((p) => (p > 0 ? 100 : 0));
      hideTimer = setTimeout(() => {
        setVisible(false);
        setPct(0);
      }, 220);
    }

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(hideTimer);
    };
  }, [isLoading]);

  return (
    <>
      {visible ? (
        <div className="pointer-events-none fixed inset-x-0 top-0 z-[100] h-[3px]">
          <div
            className="h-full bg-purple-strong shadow-[0_0_8px_rgba(88,28,207,0.6)] transition-[width,opacity] duration-200 ease-out"
            style={{ width: `${pct}%`, opacity: pct >= 100 ? 0 : 1 }}
          />
        </div>
      ) : null}
      {isLoading ? (
        <div className="pointer-events-none fixed right-4 top-4 z-[100] grid h-10 w-10 place-items-center rounded-full bg-white/90 shadow-[0_4px_16px_rgba(16,24,40,0.16)] backdrop-blur-sm">
          <Loader2 className="h-5 w-5 animate-spin text-purple-strong" />
        </div>
      ) : null}
    </>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // On a route other than "/" (a fresh load of e.g. /jobs/123), dist/index.html's
  // pre-rendered homepage body starts hidden behind a loading screen — see
  // scripts/export-static.mjs. This marks the exact moment the real page has
  // actually committed to the DOM, which is what un-hides it: useLayoutEffect
  // fires synchronously right after that commit but before the browser's next
  // paint, so the loading screen and the real content never both paint in the
  // same frame in either order (client.tsx does the equivalent for "/" itself,
  // since that path never has anything to hide in the first place).
  useLayoutEffect(() => {
    document.documentElement.setAttribute("data-app-mounted", "");
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <RouteProgressBar />
      {/* Keying on pathname re-triggers the fade-in on every route change —
          a plain component swap otherwise pops in instantly, which is most
          of what read as "jumpy" between pages. */}
      <div key={pathname} className="animate-in fade-in duration-200">
        {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
        <Outlet />
      </div>
    </QueryClientProvider>
  );
}
